import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  Hidden,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Typography
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  IBookingCreateInput,
} from "../../../../../reducers/bookings/types";
import { IProtectionPlan } from "../../../../../reducers/protectionPlan/types";
import {
  IVehicleGroupProtectionPlan,
  ProtectionPlanItem
} from "../../../../../reducers/vehiclePriceGroups/types";
import { IAppState } from "../../../../../store";
import { getAddonIcon, toCurrency } from "../../../../common/utils";

const useStyles = makeStyles(() =>
  createStyles({
    form: {
      flexGrow: 1,
      maxWidth: "100%"
    },
    addonIcon: {
      width: "100%",
      height: "75px"
    }
  })
);

interface IProtectionPlanCardProps {
  bookingData: IBookingCreateInput;
  selectedProtectionVehicleGroupPlan: IVehicleGroupProtectionPlan[];
  selectedProtectionPlan?: string;
  onSelect(ProtectionPlan?: string): void;
}
export const ProtectionPlanCard: React.FC<IProtectionPlanCardProps> = (
  props
) => {
  const { selectedProtectionVehicleGroupPlan } = props;
  const classes = useStyles();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const [selected, setSelected] = useState<boolean>(
    !!props.selectedProtectionPlan
  );
  const [values, setValues] = useState<Partial<IProtectionPlan>>({
    name: "",
    id: ""
  });

  useEffect(() => {
    if (props.selectedProtectionPlan) {
      const ProtectionPlan = props.selectedProtectionVehicleGroupPlan.find(
        (protectionPlan) =>
          protectionPlan.plan.id === props.selectedProtectionPlan
      )?.plan;
      if (ProtectionPlan) {
        setValues({ ...ProtectionPlan });
      }
      setSelected(true);
    } else {
      setValues({ name: "", id: "" });
      setSelected(false);
    }
  }, [props.selectedProtectionPlan]);

  useEffect(() => {
    if (!selected && props.selectedProtectionPlan) {
      props.onSelect();
    }
  }, [selected]);

  const uniqueProtectionVehicleGroupPlan = Array.from(
    new Set(
      selectedProtectionVehicleGroupPlan.map(
        (protectionPlan) => protectionPlan.plan.id
      )
    )
  ).map((id) => {
    const protectionPlan = selectedProtectionVehicleGroupPlan.find(
      (protectionPlan) => protectionPlan.plan.id === id
    );
    if (protectionPlan) {
      let rate = 0;
      let rateType = "";
      if (protectionPlan.hasFixedRate) {
        rate = protectionPlan.fixedRate!;
        rateType = "fixed";
      } else if (protectionPlan.recurringRate) {
        const applicableRate = protectionPlan.recurringRate.find(
          (rateItem) =>
            rateItem.rateTypeName == props.bookingData.rateTypeName
        );
        rate = applicableRate?.rate || 0;
        rateType = props.bookingData.rateTypeName;
      }
      return {
        protectionPlan: protectionPlan,
        rate,
        rateType
      };
    }
  });

  return (
    <Grid container item xs={12}>
      <Hidden smDown>
        <Grid container item xs={1} alignItems="center">
          <img
            className={classes.addonIcon}
            src={getAddonIcon("insurance")}
            alt=""
          ></img>
        </Grid>
      </Hidden>
      <Grid item xs={11}>
        <Grid item xs={12} mb={2}>
          <Typography variant="h2">
            Protection Plans
          </Typography>
        </Grid>
        <Grid
          container
          spacing={2}
          xs={12}
          justifyContent="start"
          alignItems="center"
        >
          {uniqueProtectionVehicleGroupPlan.map((singleProtectionPlan) => {
            if (singleProtectionPlan)
              return (
                  <SinglePlan
                    protectionPlanItem={singleProtectionPlan}
                    isSelected={
                      values.id === singleProtectionPlan.protectionPlan.plan.id
                    }
                    onClick={() => {
                      if(values.id === singleProtectionPlan.protectionPlan.plan.id){
                        setSelected(false);
                        props.onSelect();
                        setValues({
                          ...values,
                          id: ""
                        });
                        return;
                      }
                      props.onSelect(
                        singleProtectionPlan.protectionPlan.plan.id
                      );
                      setValues({
                        ...values,
                        id: singleProtectionPlan.protectionPlan.plan.id
                      });
                    }}
                  />
              );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

const HumanizedRate: Record<string, string> = {
  hourly: "/ hour",
  daily: "/ day",
  weekly: "/ week",
  monthly: "/ month",
  fixed: ""
};
interface ISinglePlanProps {
  protectionPlanItem: ProtectionPlanItem;
  isSelected: Boolean;
  onClick: any;
}
const SinglePlan: React.FC<ISinglePlanProps> = (props) => {
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { locale, currency } = userState.currentOrganisation;
  const { protectionPlanItem, isSelected } = props;
  return (
    <Grid item container xs={3} onClick={props.onClick} sx={{display: "block", width: "100%", height: 150}}>
      <ButtonBase
        component="div"
        sx={{
          display: "block",
          width: "100%",
          textAlign: "left",
          borderRadius: "10px"
        }}
      >
        <Grid
          container
          item
          xs={12}
          style={{
            border: isSelected ? "1px solid transparent" : "1px solid gray",
            padding: 10,
            borderRadius: "10px",
            outline: isSelected ? "2px solid black" : "none"
          }}
        >
          <Grid item xs={12}>
            <h3 style={{ marginBottom: 0, paddingBottom: 0 }}>
              {protectionPlanItem.protectionPlan.plan.name}
            </h3>
          </Grid>
          <Grid item xs={12}>
            {protectionPlanItem.protectionPlan.plan.description}
          </Grid>
          <Grid item xs={12}>
            <div style={{ fontSize: 24, marginBottom: 10, marginTop: 10 }}>
              {toCurrency(protectionPlanItem.rate, currency, locale)}
              <span style={{ fontSize: 12 }}>
                {HumanizedRate[protectionPlanItem.rateType]}
              </span>
            </div>
          </Grid>
        </Grid>
      </ButtonBase>
    </Grid>
  );
};